import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Pressable } from '../Common/Pressable'
import { useTimeline } from '../../context/useTimeline'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useNavigation } from '../../context/useNavigation'
import { CanvasOverlay } from '../Canvas/CanvasOverlay'
import { usePannableCanvasContext } from '../Canvas/pannableCanvasContext'
import type { DashboardViewProps } from '../DashboardContainer'
import type { DashboardChecklistVisualItem, RecurringOccurrenceProjection } from '../../types/dashboardVisualItem'
import type { TimelineElement } from '../../types/timelineElement'
import type { TodayContext } from '../../utils/todayContext'
import { TimeScale } from './TimeScale'
import {
  buildScaleMarkers,
  compactLabel,
  elementStartMs,
  getElementColor,
  getHoverTitle,
  getLayoutTransition,
  splitMacroElements,
} from './viewUtils'

const DAY_MS = 24 * 60 * 60 * 1000
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const getChecklistDateMs = (item: DashboardChecklistVisualItem) => {
  const raw = item.startDate ?? item.endDate
  if (!raw) return null
  const parsed = new Date(raw).getTime()
  return Number.isFinite(parsed) ? parsed : null
}

type Point = { x: number; y: number }

const buildSmoothPath = (points: Point[]) => {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`
  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] ?? points[i + 1]
    const c1x = p1.x + (p2.x - p0.x) / 6
    const c1y = p1.y + (p2.y - p0.y) / 6
    const c2x = p2.x - (p3.x - p1.x) / 6
    const c2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`
  }
  return d
}

type Placed<T> = { item: T; x: number }
const stackByTime = <T,>(items: T[], xOf: (item: T) => number, minGap: number, xMin: number, xMax: number): Placed<T>[] => {
  const sorted = [...items].sort((a, b) => xOf(a) - xOf(b))
  let cursor = -Infinity
  return sorted.map((item) => {
    const desired = clamp(xOf(item), xMin, xMax)
    const x = Math.max(desired, cursor + minGap)
    cursor = x
    return { item, x: Math.min(x, xMax) }
  })
}

const buildWeekendBands = (startMs: number, endMs: number) => {
  const duration = Math.max(1, endMs - startMs)
  const visibleDays = duration / DAY_MS
  if (visibleDays > 60) return []
  const bands: { leftPct: number; widthPct: number }[] = []
  const cursor = new Date(startMs)
  cursor.setHours(0, 0, 0, 0)
  const daysToSat = (6 - cursor.getDay() + 7) % 7
  cursor.setDate(cursor.getDate() + daysToSat)
  while (cursor.getTime() < endMs) {
    const bandStart = Math.max(cursor.getTime(), startMs)
    const bandEnd = Math.min(cursor.getTime() + 2 * DAY_MS, endMs)
    if (bandEnd > bandStart) {
      bands.push({
        leftPct: ((bandStart - startMs) / duration) * 100,
        widthPct: ((bandEnd - bandStart) / duration) * 100,
      })
    }
    cursor.setDate(cursor.getDate() + 7)
  }
  return bands
}

const cadenceDotColor = (state: RecurringOccurrenceProjection['state']) => {
  switch (state) {
    case 'completed': return { fill: 'var(--success)', ring: 'color-mix(in srgb, var(--success) 35%, transparent)' }
    case 'today':     return { fill: 'var(--info)', ring: 'color-mix(in srgb, var(--info) 55%, transparent)' }
    case 'missed':    return { fill: 'var(--warning)', ring: 'color-mix(in srgb, var(--warning) 35%, transparent)' }
    case 'blocked':   return { fill: 'var(--danger)', ring: 'color-mix(in srgb, var(--danger) 45%, transparent)' }
    case 'upcoming':
    default:          return { fill: 'color-mix(in srgb, var(--text-strong) 55%, transparent)', ring: 'color-mix(in srgb, var(--text-strong) 20%, transparent)' }
  }
}

type JourneyStageProps = {
  viewStartMs: number
  viewEndMs: number
  selectedElementId: string | null
  onElementClick: (element: TimelineElement) => void
  onChecklistClick: (checklistItemId: string) => void
  todayContext: TodayContext
  recurringOccurrencesById: ReadonlyMap<string, readonly RecurringOccurrenceProjection[]>
  calculateElementProgress: (elementId: string) => number
  currentLevel: ReturnType<typeof useNavigation>['currentLevel']
  canvasTransform: ReturnType<typeof usePannableCanvasContext>['transform']
  transition: ReturnType<typeof getLayoutTransition>
  isReducedMotion: boolean
  checklistMilestones: DashboardChecklistVisualItem[]
  goals: TimelineElement[]
  tasks: TimelineElement[]
  subTasks: TimelineElement[]
  tasksByGoalId: Map<string, TimelineElement[]>
  subTasksByTaskId: Map<string, TimelineElement[]>
}

const JourneyStage = ({
  viewStartMs,
  viewEndMs,
  selectedElementId,
  onElementClick,
  onChecklistClick,
  todayContext,
  recurringOccurrencesById,
  calculateElementProgress,
  currentLevel,
  canvasTransform,
  transition,
  isReducedMotion,
  checklistMilestones,
  goals,
  tasks,
  subTasks,
  tasksByGoalId,
  subTasksByTaskId,
}: JourneyStageProps) => {
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [stageWidth, setStageWidth] = useState(1000)
  const [stageHeightMeasured, setStageHeightMeasured] = useState(600)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  useLayoutEffect(() => {
    const node = stageRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    if (rect.width) setStageWidth(rect.width)
    if (rect.height) setStageHeightMeasured(rect.height)
    const observer = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect
      if (!r) return
      if (r.width) setStageWidth(r.width)
      if (r.height) setStageHeightMeasured(r.height)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const sparseGoals = goals.length <= 3
  const minStageHeight = currentLevel === 'dashboard' ? 420 : currentLevel === 'goal' ? 380 : 340
  const stageHeight = Math.max(stageHeightMeasured, minStageHeight)

  const windowMs = Math.max(1, viewEndMs - viewStartMs)
  const padding = sparseGoals ? 96 : 76
  const usableWidth = Math.max(120, stageWidth - padding * 2)
  const xForTime = (ms: number) => {
    const ratio = clamp((ms - viewStartMs) / windowMs, 0, 1)
    return padding + ratio * usableWidth
  }

  const goalXPlacements = useMemo(() => {
    const withDates = goals.filter((goal) => Number.isFinite(elementStartMs(goal)))
    const goalMinGap = sparseGoals ? 200 : 150
    return stackByTime(
      withDates,
      (goal) => xForTime(elementStartMs(goal)),
      goalMinGap,
      padding,
      padding + usableWidth,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals, viewStartMs, viewEndMs, stageWidth, sparseGoals])

  const taskPlacements = useMemo(
    () =>
      stackByTime(
        tasks,
        (task) => xForTime(elementStartMs(task)),
        44,
        padding,
        padding + usableWidth,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, viewStartMs, viewEndMs, stageWidth],
  )

  const subTaskPlacements = useMemo(
    () =>
      stackByTime(
        subTasks,
        (subTask) => xForTime(elementStartMs(subTask)),
        22,
        padding,
        padding + usableWidth,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subTasks, viewStartMs, viewEndMs, stageWidth],
  )

  const checklistPlacements = useMemo(() => {
    const dated = checklistMilestones
      .map((item) => ({ item, ms: getChecklistDateMs(item) }))
      .filter((entry): entry is { item: DashboardChecklistVisualItem; ms: number } => entry.ms !== null)
    return stackByTime(dated, (entry) => xForTime(entry.ms), 26, padding, padding + usableWidth)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistMilestones, viewStartMs, viewEndMs, stageWidth])

  const taskColorById = useMemo(() => {
    const map = new Map<string, string>()
    tasks.forEach((task, index) => map.set(task.id, getElementColor(task, index)))
    return map
  }, [tasks])

  const taskXById = useMemo(() => {
    const map = new Map<string, number>()
    taskPlacements.forEach(({ item, x }) => map.set(item.id, x))
    return map
  }, [taskPlacements])

  const subTaskXById = useMemo(() => {
    const map = new Map<string, { x: number; parentId?: string }>()
    subTaskPlacements.forEach(({ item, x }) => map.set(item.id, { x, parentId: item.parentId ?? undefined }))
    return map
  }, [subTaskPlacements])

  const cadencePoints = useMemo(() => {
    const dots: { key: string; x: number; state: RecurringOccurrenceProjection['state']; cadence: RecurringOccurrenceProjection['cadence']; label: string }[] = []
    for (const [sourceId, occurrences] of recurringOccurrencesById.entries()) {
      const item = checklistMilestones.find((entry) => entry.id === sourceId)
      const title = item?.title ?? 'Recurring'
      for (const occ of occurrences) {
        const anchor = occ.cadence === 'WEEKLY'
          ? (occ.occurrenceStartMs + occ.occurrenceEndMs) / 2
          : occ.occurrenceStartMs
        if (anchor < viewStartMs - DAY_MS || anchor > viewEndMs + DAY_MS) continue
        dots.push({
          key: `${sourceId}-${occ.occurrenceKey}`,
          x: xForTime(anchor),
          state: occ.state,
          cadence: occ.cadence,
          label: `${title} · ${occ.cadence === 'DAILY' ? 'Daily' : 'Weekly'} · ${occ.state}`,
        })
      }
    }
    return dots
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurringOccurrencesById, checklistMilestones, viewStartMs, viewEndMs, stageWidth])

  const lanePack = useMemo(() => {
    const laneOrder: Array<'goal' | 'cadence' | 'task' | 'subtask' | 'checklist'> = ['goal']
    if (cadencePoints.length > 0) laneOrder.push('cadence')
    if (taskPlacements.length > 0) laneOrder.push('task')
    if (subTaskPlacements.length > 0) laneOrder.push('subtask')
    if (checklistPlacements.length > 0) laneOrder.push('checklist')

    const topFrac = 0.16
    const bottomFrac = 0.90
    const pos: Partial<Record<'goal' | 'cadence' | 'task' | 'subtask' | 'checklist', number>> = {}

    if (laneOrder.length === 1) {
      pos[laneOrder[0]] = 0.5 * stageHeight
    } else {
      laneOrder.forEach((lane, i) => {
        const frac = topFrac + (i / (laneOrder.length - 1)) * (bottomFrac - topFrac)
        pos[lane] = frac * stageHeight
      })
    }

    return {
      goalLaneY: pos.goal ?? 0.5 * stageHeight,
      cadenceLaneY: pos.cadence ?? -100,
      taskLaneY: pos.task ?? -100,
      subTaskLaneY: pos.subtask ?? -100,
      checklistLaneY: pos.checklist ?? -100,
    }
  }, [cadencePoints.length, taskPlacements.length, subTaskPlacements.length, checklistPlacements.length, stageHeight])

  const { checklistLaneY, goalLaneY, cadenceLaneY, taskLaneY, subTaskLaneY } = lanePack

  const goalPlacements = useMemo(() => {
    return goalXPlacements.map(({ item, x }) => ({
      item,
      x,
      y: goalLaneY - (calculateElementProgress(item.id) - 50) * 0.36,
    }))
  }, [goalXPlacements, goalLaneY, calculateElementProgress])

  const goalXById = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>()
    goalPlacements.forEach(({ item, x, y }) => map.set(item.id, { x, y }))
    return map
  }, [goalPlacements])

  const pathPoints: Point[] = goalPlacements.length > 0
    ? goalPlacements.map(({ x, y }) => ({ x, y }))
    : []
  const pathData = buildSmoothPath(pathPoints)

  const spineTicks = useMemo(() => {
    if (goalPlacements.length < 2) return [] as { x: number; y: number }[]
    const markers = buildScaleMarkers(viewStartMs, viewEndMs, { zoom: canvasTransform.zoom })
    const first = goalPlacements[0]
    const last = goalPlacements[goalPlacements.length - 1]
    return markers
      .map((marker) => {
        const pctRaw = parseFloat(marker.left)
        const pct = Number.isFinite(pctRaw) ? pctRaw / 100 : 0
        const ms = viewStartMs + pct * windowMs
        const x = xForTime(ms)
        if (x < first.x - 4 || x > last.x + 4) return null
        let y = first.y
        for (let i = 0; i < goalPlacements.length - 1; i++) {
          const a = goalPlacements[i]
          const b = goalPlacements[i + 1]
          if (x >= a.x && x <= b.x) {
            const t = b.x === a.x ? 0 : (x - a.x) / (b.x - a.x)
            y = a.y + (b.y - a.y) * t
            break
          }
        }
        return { x, y }
      })
      .filter((point): point is { x: number; y: number } => point !== null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasTransform.zoom, goalPlacements, viewStartMs, viewEndMs, stageWidth])

  const weekendBands = useMemo(
    () => buildWeekendBands(viewStartMs, viewEndMs),
    [viewStartMs, viewEndMs],
  )
  const weekMarkers = useMemo(
    () => buildScaleMarkers(viewStartMs, viewEndMs, { zoom: canvasTransform.zoom }),
    [canvasTransform.zoom, viewStartMs, viewEndMs],
  )

  const todayX = todayContext.isTodayInWindow && todayContext.todayRatio !== null
    ? padding + todayContext.todayRatio * usableWidth
    : null


  const persistentBranches = useMemo(() => {
    const paths: { key: string; d: string; color: string; opacity: number; width: number }[] = []
    taskPlacements.forEach(({ item: task, x: taskX }) => {
      const parentId = task.parentId
      if (!parentId) return
      const goalPoint = goalXById.get(parentId)
      if (!goalPoint) return
      const color = taskColorById.get(task.id) ?? 'color-mix(in srgb, var(--info) 60%, transparent)'
      const midY = (goalPoint.y + taskLaneY) / 2
      paths.push({
        key: `pbranch-g2t-${task.id}`,
        d: `M ${goalPoint.x} ${goalPoint.y} C ${goalPoint.x} ${midY}, ${taskX} ${midY}, ${taskX} ${taskLaneY}`,
        color,
        opacity: 0.14,
        width: 1,
      })
    })
    subTaskPlacements.forEach(({ item: subTask, x: subX }) => {
      const parentId = subTask.parentId
      if (!parentId) return
      const parentTaskX = taskXById.get(parentId)
      if (parentTaskX === undefined) return
      const color = taskColorById.get(parentId) ?? 'color-mix(in srgb, var(--info) 50%, transparent)'
      const midY = (taskLaneY + subTaskLaneY) / 2
      paths.push({
        key: `pbranch-t2s-${subTask.id}`,
        d: `M ${parentTaskX} ${taskLaneY} C ${parentTaskX} ${midY}, ${subX} ${midY}, ${subX} ${subTaskLaneY}`,
        color,
        opacity: 0.16,
        width: 1,
      })
    })
    return paths
  }, [taskPlacements, subTaskPlacements, goalXById, taskXById, taskColorById, taskLaneY, subTaskLaneY])

  const hoveredIsTask = hoveredId ? tasks.some((task) => task.id === hoveredId) : false
  const hoveredIsSubTask = hoveredId ? subTasks.some((subTask) => subTask.id === hoveredId) : false
  const branchHints = useMemo(() => {
    if (!hoveredId) return [] as { key: string; d: string; color: string }[]
    const hints: { key: string; d: string; color: string }[] = []
    const buildGoalToTask = (task: (typeof tasks)[number]) => {
      const taskX = taskXById.get(task.id)
      if (taskX === undefined) return
      const parentId = task.parentId
      if (!parentId) return
      const goalPoint = goalXById.get(parentId)
      if (!goalPoint) return
      const color = taskColorById.get(task.id) ?? 'color-mix(in srgb, var(--info) 60%, transparent)'
      const midY = (goalPoint.y + taskLaneY) / 2
      hints.push({
        key: `branch-g2t-${task.id}`,
        d: `M ${taskX} ${taskLaneY} C ${taskX} ${midY}, ${goalPoint.x} ${midY}, ${goalPoint.x} ${goalPoint.y}`,
        color,
      })
    }
    const buildTaskToSubTask = (subTask: (typeof subTasks)[number]) => {
      const subX = subTaskPlacements.find((entry) => entry.item.id === subTask.id)?.x
      if (subX === undefined) return
      const parentId = subTask.parentId
      if (!parentId) return
      const parentTaskX = taskXById.get(parentId)
      if (parentTaskX === undefined) return
      const color = taskColorById.get(parentId) ?? 'color-mix(in srgb, var(--info) 50%, transparent)'
      const midY = (taskLaneY + subTaskLaneY) / 2
      hints.push({
        key: `branch-t2s-${subTask.id}`,
        d: `M ${parentTaskX} ${taskLaneY} C ${parentTaskX} ${midY}, ${subX} ${midY}, ${subX} ${subTaskLaneY}`,
        color,
      })
    }
    if (hoveredIsSubTask) {
      const subTask = subTasks.find((entry) => entry.id === hoveredId)
      if (subTask) {
        buildTaskToSubTask(subTask)
        const parentTask = subTask.parentId ? tasks.find((entry) => entry.id === subTask.parentId) : undefined
        if (parentTask) buildGoalToTask(parentTask)
      }
    } else if (hoveredIsTask) {
      const task = tasks.find((entry) => entry.id === hoveredId)
      if (task) {
        buildGoalToTask(task)
        const taskSubs = subTasksByTaskId.get(task.id) ?? []
        taskSubs.forEach(buildTaskToSubTask)
      }
    } else if (goalXById.has(hoveredId)) {
      const goalTasks = tasksByGoalId.get(hoveredId) ?? []
      goalTasks.forEach((task) => {
        buildGoalToTask(task)
        const taskSubs = subTasksByTaskId.get(task.id) ?? []
        taskSubs.forEach(buildTaskToSubTask)
      })
    }
    return hints
  }, [hoveredId, hoveredIsTask, hoveredIsSubTask, tasks, subTasks, tasksByGoalId, subTasksByTaskId, taskColorById, taskXById, subTaskPlacements, goalXById, taskLaneY, subTaskLaneY])

  const checklistBranches = useMemo(() => {
    if (checklistPlacements.length === 0) return [] as { key: string; d: string; color: string }[]
    const links: { key: string; d: string; color: string }[] = []
    checklistPlacements.forEach(({ item, x: checklistX }) => {
      const parentId = item.item.parentId
      let parentX: number | undefined
      let parentY: number | undefined
      let color = 'color-mix(in srgb, var(--info) 32%, transparent)'

      if (parentId) {
        const parentTaskX = taskXById.get(parentId)
        if (parentTaskX !== undefined) {
          parentX = parentTaskX
          parentY = taskLaneY
          color = taskColorById.get(parentId) ?? color
        } else {
          const parentSub = subTaskXById.get(parentId)
          if (parentSub) {
            parentX = parentSub.x
            parentY = subTaskLaneY
            color = parentSub.parentId ? (taskColorById.get(parentSub.parentId) ?? color) : color
          } else {
            const goalPoint = goalXById.get(parentId)
            if (goalPoint) {
              parentX = goalPoint.x
              parentY = goalPoint.y + 22
              color = 'color-mix(in srgb, var(--info) 42%, transparent)'
            }
          }
        }
      }
      if (parentX === undefined || parentY === undefined) {
        const rootGoalId = item.item.rootGoalId
        if (!rootGoalId) return
        const goalPoint = goalXById.get(rootGoalId)
        if (!goalPoint) return
        parentX = goalPoint.x
        parentY = goalPoint.y + 22
      }
      const strokeColor = item.item.isCompleted ? 'color-mix(in srgb, var(--success) 45%, transparent)' : color
      const midY = (parentY + checklistLaneY) / 2
      links.push({
        key: `checklist-branch-${item.item.id}`,
        d: `M ${checklistX} ${checklistLaneY - 8} C ${checklistX} ${midY}, ${parentX} ${midY}, ${parentX} ${parentY}`,
        color: strokeColor,
      })
    })
    return links
  }, [checklistPlacements, taskXById, subTaskXById, goalXById, taskColorById, checklistLaneY, taskLaneY, subTaskLaneY])

  const goalSizeClasses = sparseGoals ? 'px-5 py-2.5 text-[13px]' : 'px-3.5 py-1.5 text-[12px]'

  return (
    <section id="journey-view" className="grid gap-3 h-full grid-rows-[auto_minmax(0,1fr)]">
      <TimeScale startMs={viewStartMs} endMs={viewEndMs} />
      <div
        ref={stageRef}
        className="relative overflow-hidden min-h-0"
        style={{ minHeight: minStageHeight, overscrollBehavior: 'contain', touchAction: 'none' }}
      >
        <div className="pointer-events-none absolute inset-0 opacity-25 [background-image:linear-gradient(color-mix(in_srgb,var(--text-strong)_3%,transparent)_1px,transparent_1px),linear-gradient(90deg,color-mix(in_srgb,var(--text-strong)_3%,transparent)_1px,transparent_1px)] [background-size:44px_44px]" />

        {weekendBands.map((band, i) => (
          <span
            key={`weekend-${i}`}
            aria-hidden
            className="pointer-events-none absolute inset-y-0 z-0 bg-[var(--text-strong)]/[0.025]"
            style={{ left: `${band.leftPct}%`, width: `${band.widthPct}%` }}
          />
        ))}

        {weekMarkers.map((marker) => (
          <span
            key={`grid-${marker.id}`}
            aria-hidden
            className="pointer-events-none absolute inset-y-4 z-0 w-px bg-[var(--text-strong)]/[0.05]"
            style={{ left: marker.left }}
          />
        ))}

        {todayX !== null && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 z-0"
            style={{
              left: todayX - 18,
              width: 36,
              background: 'linear-gradient(to right, transparent, color-mix(in srgb, var(--warning) 12%, transparent) 50%, transparent)',
            }}
          />
        )}

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-32"
          style={{
            background: 'linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--info) 5%, transparent) 60%, color-mix(in srgb, var(--info) 8%, transparent) 100%)',
          }}
        />

        <svg
          className="pointer-events-none absolute inset-0 z-10"
          width={stageWidth}
          height={stageHeight}
          viewBox={`0 0 ${Math.max(1, stageWidth)} ${stageHeight}`}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="journeyPathStroke" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" style={{ stopColor: 'color-mix(in srgb, var(--info) 75%, transparent)' }} />
              <stop offset="50%" style={{ stopColor: 'var(--info)' }} />
              <stop offset="100%" style={{ stopColor: 'color-mix(in srgb, var(--chart-1) 85%, transparent)' }} />
            </linearGradient>
            <linearGradient id="journeyPathHalo" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" style={{ stopColor: 'color-mix(in srgb, var(--info) 32%, transparent)' }} />
              <stop offset="100%" style={{ stopColor: 'color-mix(in srgb, var(--chart-1) 32%, transparent)' }} />
            </linearGradient>
            <linearGradient id="journeyPathHaze" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" style={{ stopColor: 'color-mix(in srgb, var(--info) 14%, transparent)' }} />
              <stop offset="100%" style={{ stopColor: 'color-mix(in srgb, var(--chart-1) 14%, transparent)' }} />
            </linearGradient>
          </defs>

          {pathData && (
            <>
              <motion.path
                d={pathData}
                fill="none"
                stroke="url(#journeyPathHaze)"
                strokeWidth={16}
                strokeLinecap="round"
                initial={isReducedMotion ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={isReducedMotion ? transition : { duration: 0.9, ease: 'easeOut' }}
              />
              <motion.path
                d={pathData}
                fill="none"
                stroke="url(#journeyPathHalo)"
                strokeWidth={8}
                strokeLinecap="round"
                initial={isReducedMotion ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={isReducedMotion ? transition : { duration: 0.9, ease: 'easeOut' }}
              />
              <motion.path
                d={pathData}
                fill="none"
                stroke="url(#journeyPathStroke)"
                strokeWidth={2.25}
                strokeLinecap="round"
                initial={isReducedMotion ? false : { pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={isReducedMotion ? transition : { duration: 0.9, ease: 'easeOut' }}
              />
              {!isReducedMotion && (
                <motion.path
                  d={pathData}
                  fill="none"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeDasharray="6 220"
                  initial={{ strokeDashoffset: 0 }}
                  animate={{ strokeDashoffset: -900 }}
                  transition={{ repeat: Infinity, duration: 9, ease: 'linear' }}
                  style={{ opacity: 0.55, mixBlendMode: 'plus-lighter', stroke: 'color-mix(in srgb, var(--text-strong) 75%, transparent)' }}
                />
              )}
            </>
          )}

          {spineTicks.map((tick, i) => (
            <line
              key={`spine-tick-${i}`}
              x1={tick.x}
              y1={tick.y - 6}
              x2={tick.x}
              y2={tick.y + 6}
              style={{ stroke: 'color-mix(in srgb, var(--info) 35%, transparent)' }}
              strokeWidth={1}
              strokeLinecap="round"
            />
          ))}

          {persistentBranches.map((branch) => (
            <path
              key={branch.key}
              d={branch.d}
              fill="none"
              stroke={branch.color}
              strokeOpacity={branch.opacity}
              strokeWidth={branch.width}
              strokeLinecap="round"
            />
          ))}

          {checklistBranches.map((link) => (
            <path
              key={link.key}
              d={link.d}
              fill="none"
              stroke={link.color}
              strokeOpacity={0.55}
              strokeWidth={1.1}
              strokeLinecap="round"
              strokeDasharray="3 4"
            />
          ))}

          {branchHints.map((hint) => (
            <path
              key={hint.key}
              d={hint.d}
              fill="none"
              stroke={hint.color}
              strokeOpacity={0.55}
              strokeWidth={1.4}
              strokeLinecap="round"
              strokeDasharray="4 3"
            />
          ))}

          {todayX !== null && (
            <>
              <line
                x1={todayX}
                y1={16}
                x2={todayX}
                y2={stageHeight - 16}
                style={{ stroke: 'color-mix(in srgb, var(--warning) 55%, transparent)' }}
                strokeWidth={1}
                strokeDasharray="3 6"
              />
              <line
                x1={todayX}
                y1={goalLaneY - 38}
                x2={todayX}
                y2={goalLaneY + 38}
                style={{ stroke: 'var(--warning)' }}
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            </>
          )}
        </svg>

        {todayX !== null && (
          <CanvasOverlay anchor="world" x={todayX} y={0}>
            <span
              className="pointer-events-none absolute z-20 -translate-x-1/2 rounded-b border border-[var(--warning)] bg-[var(--warning-soft)] px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))] shadow-[var(--shadow-card)]"
              style={{ left: 0, top: 0 }}
            >
              Today
            </span>
          </CanvasOverlay>
        )}

        {checklistPlacements.map(({ item, x }) => {
          return (
            <div key={`journey-checklist-${item.item.id}`}>
              <Pressable
                className={`absolute z-40 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rotate-45 cursor-pointer rounded-[3px] border shadow-md transition-transform hover:scale-125 ${
                  item.item.isCompleted ? 'border-[var(--success)] bg-[var(--success)]' : 'border-[var(--border-strong)] bg-[var(--text-strong)]'
                }`}
                style={{
                  left: x,
                  top: checklistLaneY,
                  boxShadow: `var(--shadow-card)`,
                }}
                aria-label={`Open checklist ${item.item.title}`}
                title={item.item.title}
                data-no-dashboard-drag="true"
                data-hover-info={`${item.item.isCompleted ? 'Done' : 'Open'} checklist: ${item.item.title}`}
                onClick={(event) => {
                  event.stopPropagation()
                  onChecklistClick(item.item.id)
                }}
              >
                <span className="sr-only">{item.item.title}</span>
              </Pressable>
            </div>
          )
        })}

        {cadencePoints.length > 0 && (
          <>
            <span
              aria-hidden
              className="pointer-events-none absolute z-0"
              style={{
                left: padding,
                width: usableWidth,
                top: cadenceLaneY - 8,
                height: 16,
                background: 'linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--info) 5%, transparent) 50%, transparent 100%)',
                borderTop: '1px dashed color-mix(in srgb, var(--info) 10%, transparent)',
                borderBottom: '1px dashed color-mix(in srgb, var(--info) 10%, transparent)',
              }}
            />
            <span
              className="pointer-events-none absolute z-20 text-[8px] font-black uppercase tracking-[0.18em] text-[var(--muted)]/70"
              style={{ left: padding - 60, top: cadenceLaneY - 6 }}
            >
              Cadence
            </span>
            {cadencePoints.map((dot) => {
              const palette = cadenceDotColor(dot.state)
              const size = dot.cadence === 'WEEKLY' ? 7 : 5
              return (
                <span
                  key={dot.key}
                  className="pointer-events-auto absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{
                    left: dot.x,
                    top: cadenceLaneY,
                    width: size,
                    height: size,
                    background: palette.fill,
                    border: `1px solid ${palette.ring}`,
                  }}
                  title={dot.label}
                  data-hover-info={dot.label}
                />
              )
            })}
          </>
        )}

        {goalPlacements.map(({ item: goal, x, y }, index) => {
          const goalTasks = tasksByGoalId.get(goal.id) ?? []
          const progress = calculateElementProgress(goal.id)
          const isSelected = selectedElementId === goal.id

          return (
            <div key={goal.id}>
              <span
                aria-hidden
                className="pointer-events-none absolute z-20 -translate-y-1/2 text-[16px] font-black text-[var(--muted)]/50"
                style={{ left: x - (sparseGoals ? 96 : 78), top: y, lineHeight: 1 }}
              >
                ⟨
              </span>
              <span
                aria-hidden
                className="pointer-events-none absolute z-20 -translate-y-1/2 text-[16px] font-black text-[var(--muted)]/50"
                style={{ left: x + (sparseGoals ? 90 : 72), top: y, lineHeight: 1 }}
              >
                ⟩
              </span>
              <motion.div
                layout
                layoutId={goal.id}
                transition={transition}
                role="button"
                tabIndex={0}
                aria-label={`Goal ${goal.title}, ${progress}% complete`}
                className={`absolute z-30 flex max-w-[22rem] -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center gap-2.5 rounded-full border bg-[var(--text-strong)] font-black text-[var(--bg)] shadow-xl transition-shadow duration-200 ${goalSizeClasses} ${
                  isSelected
                    ? 'border-[var(--info)] ring-4 ring-[var(--info)] shadow-[var(--shadow-card)]'
                    : 'border-[var(--border)] hover:shadow-[var(--shadow-card)]'
                }`}
                style={{ left: x, top: y }}
                onClick={() => onElementClick(goal)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onElementClick(goal)
                }}
                onPointerEnter={() => setHoveredId(goal.id)}
                onPointerLeave={() => setHoveredId((current) => (current === goal.id ? null : current))}
                onFocus={() => setHoveredId(goal.id)}
                onBlur={() => setHoveredId((current) => (current === goal.id ? null : current))}
                data-hover-info={getHoverTitle(goal)}
              >
                <span className={`grid ${sparseGoals ? 'h-7 w-7 text-[11px]' : 'h-6 w-6 text-[10px]'} place-items-center rounded-full bg-[var(--bg)] font-black text-[var(--text-strong)]`}>
                  {index + 1}
                </span>
                <span className="truncate">{compactLabel(goal.title, sparseGoals ? 36 : 32)}</span>
                <span className="shrink-0 rounded-full bg-[var(--bg)]/80 px-1.5 py-0.5 text-[10px]">{progress}%</span>
                <span className="shrink-0 text-[10px] opacity-60">{goalTasks.length}</span>
              </motion.div>
            </div>
          )
        })}

        {taskPlacements.map(({ item: task, x }, index) => {
          const isSelected = selectedElementId === task.id
          const isHovered = hoveredId === task.id
          const showLabel = isSelected || isHovered
          const color = getElementColor(task, index)
          const ringSize = sparseGoals ? 22 : 18
          const coreSize = sparseGoals ? 12 : 10
          return (
            <div key={task.id}>
              <span
                aria-hidden
                className={`pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full transition-opacity duration-150 ${
                  showLabel ? 'opacity-100' : 'opacity-60'
                }`}
                style={{
                  left: x,
                  top: taskLaneY,
                  width: ringSize,
                  height: ringSize,
                  border: `1.5px solid ${color}`,
                  background: 'transparent',
                }}
              />
              <motion.div
                layout
                layoutId={task.id}
                transition={transition}
                role="button"
                tabIndex={0}
                aria-label={`Task ${task.title}`}
                className={`absolute z-30 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full shadow-lg ring-2 transition-transform duration-150 ${
                  isSelected
                    ? 'ring-[var(--border-strong)] shadow-[var(--shadow-card)] scale-125'
                    : 'ring-[var(--border)] hover:scale-125 hover:ring-[var(--border-strong)]'
                }`}
                style={{
                  left: x,
                  top: taskLaneY,
                  width: coreSize,
                  height: coreSize,
                  backgroundColor: color,
                }}
                onClick={(event) => {
                  event.stopPropagation()
                  onElementClick(task)
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.stopPropagation()
                  onElementClick(task)
                }}
                onPointerEnter={() => setHoveredId(task.id)}
                onPointerLeave={() => setHoveredId((current) => (current === task.id ? null : current))}
                onFocus={() => setHoveredId(task.id)}
                onBlur={() => setHoveredId((current) => (current === task.id ? null : current))}
                data-hover-info={getHoverTitle(task)}
              />
              {showLabel && (
                <motion.span
                  key={`${task.id}-label`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.14, ease: 'easeOut' }}
                  className="pointer-events-none absolute z-40 -translate-x-1/2 whitespace-nowrap rounded-md border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-[10px] font-black text-[var(--text-strong)] shadow-lg"
                  style={{ left: x, top: taskLaneY + 20 }}
                >
                  {compactLabel(task.title, 24)}
                </motion.span>
              )}
            </div>
          )
        })}

        {subTaskPlacements.map(({ item: subTask, x }, index) => {
          const isSelected = selectedElementId === subTask.id
          const isHovered = hoveredId === subTask.id
          const showLabel = isSelected || isHovered
          const parentColor = subTask.parentId ? taskColorById.get(subTask.parentId) : undefined
          const color = parentColor ?? getElementColor(subTask, index)
          return (
            <div key={subTask.id}>
              <motion.span
                layout
                layoutId={subTask.id}
                transition={transition}
                role="button"
                tabIndex={0}
                aria-label={`Subtask ${subTask.title}`}
                className={`absolute z-30 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full shadow-md ring-1 transition-transform duration-150 ${
                  isSelected
                    ? 'ring-[var(--border-strong)] shadow-[var(--shadow-card)] scale-150'
                    : 'ring-[var(--border)] hover:scale-150 hover:ring-[var(--border-strong)]'
                }`}
                style={{ left: x, top: subTaskLaneY, backgroundColor: color, opacity: 0.85 }}
                onClick={(event) => {
                  event.stopPropagation()
                  onElementClick(subTask)
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.stopPropagation()
                  onElementClick(subTask)
                }}
                onPointerEnter={() => setHoveredId(subTask.id)}
                onPointerLeave={() => setHoveredId((current) => (current === subTask.id ? null : current))}
                data-hover-info={getHoverTitle(subTask)}
              />
              {showLabel && (
                <motion.span
                  key={`${subTask.id}-label`}
                  initial={{ opacity: 0, y: -3 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.14, ease: 'easeOut' }}
                  className="pointer-events-none absolute z-40 -translate-x-1/2 whitespace-nowrap rounded-md border border-[var(--border)] bg-[var(--surface)] px-1.5 py-0.5 text-[9px] font-bold text-[var(--muted)] shadow"
                  style={{ left: x, top: subTaskLaneY + 12 }}
                >
                  {compactLabel(subTask.title, 22)}
                </motion.span>
              )}
            </div>
          )
        })}

        <div className="pointer-events-none absolute inset-x-0 z-0 mx-16 border-t border-[var(--border-subtle)]" style={{ top: checklistLaneY + 12 }} />
        <div className="pointer-events-none absolute inset-x-0 z-0 mx-16 border-t border-[var(--border-subtle)]" style={{ top: taskLaneY + 12 }} />
        <div className="pointer-events-none absolute inset-x-0 z-0 mx-16 border-t border-[var(--border-subtle)]" style={{ top: subTaskLaneY + 10 }} />
      </div>
    </section>
  )
}

export const JourneyView = ({
  elements,
  visualItems,
  viewStartMs,
  viewEndMs,
  selectedElementId,
  onElementClick,
  onChecklistClick,
  todayContext,
  recurringOccurrencesById,
}: DashboardViewProps) => {
  const { calculateElementProgress } = useTimeline()
  const { preferences } = useClientPreferences()
  const { currentLevel } = useNavigation()
  const { transform: canvasTransform } = usePannableCanvasContext()
  const { goals, tasks, subTasks, tasksByGoalId, subTasksByTaskId } = splitMacroElements(elements)
  const transition = getLayoutTransition(preferences.animationProfile)
  const isReducedMotion = preferences.animationProfile === 'REDUCED_MOTION'
  const checklistMilestones = visualItems.filter(
    (item): item is DashboardChecklistVisualItem => item.kind === 'CHECKLIST' && !item.isUnscheduled,
  )

  if (goals.length === 0) {
    return (
      <div className="grid min-h-80 place-items-center gap-1 text-center">
        <p className="text-sm font-black uppercase tracking-[0.18em] text-[var(--muted)]">Journey unset</p>
        <p className="text-xs text-[var(--muted)]">No goals on the path yet.</p>
      </div>
    )
  }

  return (
    <JourneyStage
      viewStartMs={viewStartMs}
      viewEndMs={viewEndMs}
      selectedElementId={selectedElementId}
      onElementClick={onElementClick}
      onChecklistClick={onChecklistClick}
      todayContext={todayContext}
      recurringOccurrencesById={recurringOccurrencesById}
      calculateElementProgress={calculateElementProgress}
      currentLevel={currentLevel}
      canvasTransform={canvasTransform}
      transition={transition}
      isReducedMotion={isReducedMotion}
      checklistMilestones={checklistMilestones}
      goals={goals}
      tasks={tasks}
      subTasks={subTasks}
      tasksByGoalId={tasksByGoalId}
      subTasksByTaskId={subTasksByTaskId}
    />
  )
}

